import { useEffect } from 'react';

const SITE_URL = 'https://greenlightcity.co.za';

const upsertMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);
  const created = !element;
  const previousContent = element?.getAttribute('content');

  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
  return { element, created, previousContent };
};

export const Seo = ({
  title,
  description,
  path,
  schemas = [],
  image,
  type = 'website',
  robots = 'index, follow',
}) => {
  useEffect(() => {
    const canonicalUrl = `${SITE_URL}${path}`;
    const previousTitle = document.title;

    document.title = title;

    const metaRecords = [
      upsertMeta('meta[name="description"]', { name: 'description', content: description }),
      upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title }),
      upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description }),
      upsertMeta('meta[property="og:type"]', { property: 'og:type', content: type }),
      upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl }),
      upsertMeta('meta[name="robots"]', { name: 'robots', content: robots }),
      upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: image ? 'summary_large_image' : 'summary' }),
      upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title }),
      upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description }),
    ];

    if (image) {
      metaRecords.push(
        upsertMeta('meta[property="og:image"]', { property: 'og:image', content: image }),
        upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: image })
      );
    }

    let canonical = document.head.querySelector('link[rel="canonical"]');
    const canonicalCreated = !canonical;
    const existingCanonical = canonical?.getAttribute('href');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    const validSchemas = (Array.isArray(schemas) ? schemas : [schemas]).filter((schema) => (
      schema && typeof schema === 'object' && !Array.isArray(schema) && typeof schema['@context'] === 'string'
    ));
    const schemaScript = validSchemas.length ? document.createElement('script') : null;
    if (schemaScript) {
      schemaScript.type = 'application/ld+json';
      schemaScript.dataset.routeSchema = path;
      schemaScript.textContent = JSON.stringify(validSchemas);
      document.head.appendChild(schemaScript);
    }

    return () => {
      document.title = previousTitle;
      metaRecords.forEach(({ element, created, previousContent }) => {
        if (created) {
          element.remove();
        } else if (previousContent !== null && previousContent !== undefined) {
          element.setAttribute('content', previousContent);
        } else {
          element.removeAttribute('content');
        }
      });

      if (canonicalCreated) {
        canonical.remove();
      } else if (existingCanonical) {
        canonical.setAttribute('href', existingCanonical);
      } else {
        canonical.removeAttribute('href');
      }

      schemaScript?.remove();
    };
  }, [description, image, path, robots, schemas, title, type]);

  return null;
};
